/**
 * OpenTelemetry Browser Instrumentation for Publishing Dashboard
 *
 * This sets up distributed tracing in the browser:
 *
 * 1. Creates a TracerProvider with W3C Trace Context propagation
 * 2. Registers FetchInstrumentation — automatically wraps every fetch() call to
 *    inject a `traceparent` header so the backend can correlate spans
 * 3. Optionally exports spans to an OTLP endpoint
 *
 * This module is self-initializing — importing it triggers setup.
 * Import it as the first line of main.tsx to ensure fetch is patched
 * before any other modules make network calls.
 *
 * Environment variables:
 *   VITE_OTEL_EXPORTER_URL  — OTLP HTTP endpoint (e.g. https://your-collector:4318/v1/traces)
 *   VITE_OTEL_ENABLED       — set to "true" to enable (disabled by default to avoid noise in dev)
 */

import type { SpanProcessor } from "@opentelemetry/sdk-trace-web";

import { ZoneContextManager } from "@opentelemetry/context-zone";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  SimpleSpanProcessor,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";

/** Guard against multiple initialisations (e.g. HMR re-evaluation) */
const INIT_KEY = "__otelPubDashInitialized";

function initTelemetry() {
  if ((globalThis as Record<string, unknown>)[INIT_KEY] === true) {
    return;
  }
  (globalThis as Record<string, unknown>)[INIT_KEY] = true;

  const isEnabled = import.meta.env.VITE_OTEL_ENABLED === "true";
  const exporterUrl = (import.meta.env.VITE_OTEL_EXPORTER_URL as string) ?? "";
  const stage = (import.meta.env.VITE_STAGE as string) ?? "local";

  const isDev = stage === "local" || stage === "dev";

  if (!isEnabled) {
    if (isDev) {
      console.log(
        "[OTEL] Telemetry disabled. Set VITE_OTEL_ENABLED=true to enable.",
      );
    }
    return;
  }

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: "publishing-dashboard",
    "deployment.environment": stage,
  });

  // Build span processors list based on environment
  const spanProcessors: SpanProcessor[] = [];

  if (exporterUrl !== "") {
    const otlpExporter = new OTLPTraceExporter({ url: exporterUrl });
    spanProcessors.push(new BatchSpanProcessor(otlpExporter));
  }

  if (isDev) {
    spanProcessors.push(new SimpleSpanProcessor(new ConsoleSpanExporter()));
  }

  const provider = new WebTracerProvider({
    resource,
    spanProcessors,
  });

  provider.register({
    propagator: new W3CTraceContextPropagator(),
    contextManager: new ZoneContextManager(),
  });

  // Fetch instrumentation — monkey-patches global fetch() to inject `traceparent`.
  // Only propagate trace headers to our own API origins.
  // Match all SA-API subdomains (uat, prd, clone-N) plus any VITE_API/VITE_API_OLD.
  const corsUrls: Array<string | RegExp> = [
    // Match all *.publishingdashboard.com origins (covers uat, prd, clone-1..10)
    /^https:\/\/[a-z0-9-]+\.publishingdashboard\.com/i,
  ];

  const apiUrl = (import.meta.env.VITE_API as string) ?? "";
  if (apiUrl !== "") {
    try {
      const origin = new URL(apiUrl).origin;
      corsUrls.push(new RegExp(`^${escapeRegExp(origin)}`, "i"));
    }
    catch {
      corsUrls.push(new RegExp(`^${escapeRegExp(apiUrl)}`, "i"));
    }
  }

  const apiOldUrl = (import.meta.env.VITE_API_OLD as string) ?? "";
  if (apiOldUrl !== "") {
    try {
      const origin = new URL(apiOldUrl).origin;
      corsUrls.push(new RegExp(`^${escapeRegExp(origin)}`, "i"));
    }
    catch {
      corsUrls.push(new RegExp(`^${escapeRegExp(apiOldUrl)}`, "i"));
    }
  }

  const fetchInstrumentation = new FetchInstrumentation({
    propagateTraceHeaderCorsUrls: corsUrls,
    clearTimingResources: true,
  });

  fetchInstrumentation.setTracerProvider(provider);
  fetchInstrumentation.enable();

  console.log(
    `[OTEL] Telemetry initialized for publishing-dashboard (${stage})`,
    exporterUrl ? `→ exporting to ${exporterUrl}` : "→ console only",
  );
}

/** Escape special regex characters in a string (for matching URLs) */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Self-initialise on import — must be the first import in main.tsx
initTelemetry();
